import React, { useEffect } from "react"
import {
    Avatar,
    Box,
    Button,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import ExtensionIcon from "@material-ui/icons/Extension"
import InfoIcon from "@material-ui/icons/Info"
import LaunchIcon from "@material-ui/icons/Launch"
import MotorcycleIcon from "@material-ui/icons/Motorcycle"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import { useHistory } from "react-router-dom"
import { ParametrosIntegracaoActionsTypes } from "../context/parametrosIntegracaoActions"
import { useParametrosIntegracaoContext } from "../context/useParametrosIntegracaoContext"
import RepresentanteSelector from "../../../functions/RepresentanteSelector"
import { CARDS_INTEGRACAO } from "../models/parametrosIntegracaoModel"
import type {
    CampoIntegracao,
    CardIntegracao,
    ParametrosIntegracaoModel,
} from "../models/parametrosIntegracaoModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        width: "100%",
        maxWidth: "100%",
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("sm")]: { borderRadius: 15 },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.05rem" },
    },
    logo: {
        width: 44,
        height: 44,
        marginRight: theme.spacing(2),
        backgroundColor: "white",
        "& img": { objectFit: "contain" },
    },
    cardContent: {
        padding: theme.spacing(3),
        flexGrow: 1,
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    descricaoCardText: {
        color: "#607d8b",
        fontSize: "0.9rem",
        lineHeight: 1.5,
    },
    checkboxRow: {
        display: "flex",
        alignItems: "center",
    },
    infoIcon: {
        fontSize: 16,
        color: "#90a4ae",
        marginLeft: theme.spacing(0.5),
        cursor: "pointer",
    },
    linkButton: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        color: "#4caf50",
        borderColor: "#4caf50",
        "&:hover": { borderColor: "#388e3c", backgroundColor: "rgba(76,175,80,0.08)" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        padding: "10px 32px",
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#388e3c", boxShadow: "0 6px 16px rgba(76,175,80,0.4)" },
    },
}))

export function FormularioParametrosIntegracao() {
    const classes = useStyles()
    const { parametros, dispatch, salvar, erroCarregamento } = useParametrosIntegracaoContext()
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handleCampo = (
        campo: keyof ParametrosIntegracaoModel,
        valor: string | number | boolean | null
    ) => {
        dispatch({ type: ParametrosIntegracaoActionsTypes.ATUALIZAR_CAMPO, payload: { campo, valor } })
    }

    // Abre uma página do sistema administrativo (web) em nova aba, montando a URL a
    // partir do representante atual (Cliente Fiel / white-label). Ex.: a ação
    // gerarlinkintegracao99 gera o link de autorização da 99 Food e redireciona a aba
    // para ele. Depende da sessão web do sistema administrativo (mesmo domínio).
    const abrirAcaoWeb = (caminho: string) => {
        const base = RepresentanteSelector.getRepresentante()
            .getUrlSistemaAdministrativo()
            .replace(/\/+$/, "")
        const rel = caminho.replace(/^\/+/, "")
        window.open(`${base}/${rel}`, "_blank", "noopener")
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Integrações salvas com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar", "error")
        }
    }

    const renderCampo = (campo: CampoIntegracao) => {
        if (campo.tipo === "switch") {
            return (
                <Grid item xs={12} key={campo.campo}>
                    <Box className={classes.checkboxRow}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={parametros[campo.campo] as boolean}
                                    onChange={e => {
                                        const checked = e.target.checked
                                        handleCampo(campo.campo, checked)
                                    }}
                                    style={{ color: "#4caf50" }}
                                />
                            }
                            label={campo.label}
                        />
                        {campo.descricao && (
                            <Tooltip title={campo.descricao} placement="right">
                                <InfoIcon className={classes.infoIcon} />
                            </Tooltip>
                        )}
                    </Box>
                </Grid>
            )
        }

        const valorAtual = parametros[campo.campo]
        return (
            <Grid item xs={12} key={campo.campo}>
                <TextField
                    label={campo.label}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type={campo.tipo === "number" ? "number" : "text"}
                    value={valorAtual ?? ""}
                    onChange={e => {
                        const v = e.target.value
                        if (campo.tipo === "number") {
                            handleCampo(campo.campo, v === "" ? null : Number(v))
                        } else {
                            handleCampo(campo.campo, v === "" ? null : v)
                        }
                    }}
                    InputProps={
                        campo.descricao
                            ? {
                                  endAdornment: (
                                      <Tooltip title={campo.descricao} placement="top">
                                          <InfoIcon className={classes.infoIcon} />
                                      </Tooltip>
                                  ),
                              }
                            : undefined
                    }
                />
            </Grid>
        )
    }

    const renderCard = (card: CardIntegracao) => {
        if (card.internoSomente && !parametros.interno) return null

        return (
            <Grid item xs={12} sm={6} lg={4} key={card.id} style={{ display: "flex" }}>
                <Paper className={classes.configCard}>
                <Box className={classes.cardHeader}>
                    {/* Avatar único: se houver logo válida, mostra a imagem; se a logo
                        estiver ausente/quebrada (ou não houver logoUrl), o MUI cai para o
                        children — moto (Foody) ou ícone genérico de integração. */}
                    <Avatar
                        src={card.logoUrl || undefined}
                        alt={card.titulo}
                        className={classes.logo}
                        variant="rounded"
                        style={{
                            backgroundColor: card.logoUrl
                                ? "white"
                                : card.icone === "motorcycle"
                                ? "#fdecea"
                                : "#eceff1",
                        }}
                    >
                        {card.icone === "motorcycle" ? (
                            <MotorcycleIcon style={{ color: "#d93025" }} />
                        ) : (
                            <ExtensionIcon style={{ color: "#607d8b" }} />
                        )}
                    </Avatar>
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        {card.titulo}
                        {card.internoSomente && (
                            <Typography
                                component="span"
                                style={{ color: "#90a4ae", fontSize: "0.8rem", marginLeft: 8 }}
                            >
                                (Acesso restrito — Cliente Fiel)
                            </Typography>
                        )}
                    </Typography>
                </Box>

                <Box className={classes.cardContent}>
                    {card.descricaoCard && (
                        <Typography className={classes.descricaoCardText}>
                            {card.descricaoCard}
                        </Typography>
                    )}

                    {card.campos && (
                        <Grid container spacing={2} alignItems="center">
                            {card.campos.map(renderCampo)}
                        </Grid>
                    )}

                    {card.acaoWeb && (
                        <Box mt={card.campos || card.descricaoCard ? 2 : 0}>
                            <Button
                                variant="outlined"
                                className={classes.linkButton}
                                startIcon={<LaunchIcon />}
                                onClick={() => abrirAcaoWeb(card.acaoWeb!.caminho)}
                            >
                                {card.acaoWeb.label}
                            </Button>
                            {card.acaoWeb.descricao && (
                                <Typography
                                    variant="caption"
                                    display="block"
                                    style={{ color: "#90a4ae", marginTop: 6 }}
                                >
                                    {card.acaoWeb.descricao}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {card.link && (
                        <Box mt={card.campos || card.descricaoCard ? 2 : 0}>
                            <Tooltip
                                title={card.link.futuro ? "Tela em migração — disponível em breve" : ""}
                                placement="top"
                            >
                                <span>
                                    <Button
                                        variant="outlined"
                                        className={classes.linkButton}
                                        startIcon={<LaunchIcon />}
                                        onClick={() => history.push(card.link!.rota)}
                                    >
                                        {card.link.label}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                </Paper>
            </Grid>
        )
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Parâmetros de Integração
                </Typography>

                <Grid container spacing={3} alignItems="stretch">
                    {CARDS_INTEGRACAO.map(renderCard)}
                </Grid>

                <Box display="flex" justifyContent="center" mt={4} mb={4}>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
